// pages/Services.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import ServicesSection from '../components/home/ServicesSection';
import { Calendar, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const Services = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <div className="hero-gradient text-white">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl font-extrabold sm:text-5xl md:text-6xl">
              Our Services
            </h1>
            <p className="mt-6 max-w-2xl mx-auto text-xl text-blue-100">
              Comprehensive dental care solutions for your entire family
            </p>
          </motion.div>
        </div>
      </div>

      {/* Main Services Section */}
      <ServicesSection />

      {/* Detailed Services Information */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Preventive Dentistry */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-20"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Preventive Dentistry</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              <div>
                <p className="text-gray-500 mb-6 leading-relaxed">
                  Our preventive dental care helps maintain your oral health and prevent future problems.
                  Regular check-ups and cleanings are essential for a healthy smile.
                </p>
                <ul className="space-y-3">
                  {['Regular dental check-ups and cleanings', 'Dental X-rays and examinations', 'Fluoride treatments', 'Dental sealants'].map((item, i) => (
                    <li key={i} className="flex items-center text-gray-600">
                      <CheckCircle className="h-5 w-5 text-blue-500 mr-3 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <img
                  src="/images/services/preventive-dentistry.jpg"
                  alt="Preventive Dentistry"
                  className="rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                />
              </div>
            </div>
          </motion.div>

          {/* Restorative Dentistry */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-20"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Restorative Dentistry</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              <div>
                <img
                  src="/images/services/restorativedentistry.jpg"
                  alt="Restorative Dentistry"
                  className="rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                />
              </div>
              <div>
                <p className="text-gray-500 mb-6 leading-relaxed">
                  We offer comprehensive restorative dental services to repair damaged teeth
                  and restore your beautiful smile.
                </p>
                <ul className="space-y-3">
                  {['Dental fillings', 'Dental crowns and bridges', 'Root canal therapy', 'Dental implants'].map((item, i) => (
                    <li key={i} className="flex items-center text-gray-600">
                      <CheckCircle className="h-5 w-5 text-blue-500 mr-3 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="hero-gradient rounded-2xl shadow-xl mt-12"
          >
            <div className="px-8 py-14 text-center">
              <h3 className="text-2xl font-bold text-white mb-4">
                Ready to Schedule Your Appointment?
              </h3>
              <p className="text-blue-100 mb-8 max-w-lg mx-auto">
                Contact us today to schedule a consultation with our experienced dental team.
              </p>
              <Link
                to="/appointment"
                className="inline-flex items-center px-8 py-3.5 border border-transparent text-base font-bold rounded-lg text-blue-600 bg-white hover:bg-blue-50 transition-all duration-300 hover:shadow-xl hover:scale-105"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Appointment
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default Services;